import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardGlass } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Heart, Sparkles, QrCode, Image, Music, MessageSquare, Check, ArrowRight } from 'lucide-react';
import { Header } from '@/components/Header';
import { HeroSection } from '@/components/landing/HeroSection';
import { TestimonialsSection } from '@/components/landing/TestimonialsSection';
import { OFFICIAL_CREATE_URL } from '@/lib/constants';
const themes = [{
  id: 'amoureux',
  label: 'Amour',
  emoji: '💕',
  description: 'Pour votre moitié'
}, {
  id: 'parent',
  label: 'Parent',
  emoji: '👨‍👩‍👧‍👦',
  description: 'Pour vos parents'
}, {
  id: 'meilleur_ami',
  label: 'Meilleur(e) ami(e)',
  emoji: '🤝',
  description: 'Pour votre meilleur(e) ami(e)'
}, {
  id: 'ami',
  label: 'Ami(e)',
  emoji: '🌟',
  description: 'Pour vos amis'
}, {
  id: 'collegue',
  label: 'Collègue',
  emoji: '💼',
  description: 'Pour vos collègues'
}];
const features = [{
  icon: Image,
  title: 'Photos illimitées',
  description: 'Ajoutez plusieurs photos qui défilent en carrousel automatique'
}, {
  icon: Music,
  title: 'Message audio',
  description: 'Enregistrez votre voix pour un moment encore plus personnel'
}, {
  icon: MessageSquare,
  title: 'Message personnalisé',
  description: 'Écrivez un texte qui sera révélé avec une animation'
}, {
  icon: QrCode,
  title: 'QR Code unique',
  description: 'Un code scannable à vie, sans abonnement mensuel'
}];
const steps = [{
  number: '01',
  title: 'Choisissez votre relation',
  description: 'Sélectionnez le type de lien : amour, famille, amitié... L\'ambiance s\'adapte automatiquement.'
}, {
  number: '02',
  title: 'Personnalisez votre page',
  description: 'Ajoutez vos photos, votre message texte, un audio personnel et choisissez vos couleurs.'
}, {
  number: '03',
  title: 'Générez votre QR Code',
  description: 'Votre QR Portrait est créé instantanément. Téléchargez-le ou commandez le cadre physique.'
}, {
  number: '04',
  title: 'Offrez une émotion',
  description: 'La personne scanne le QR Code et découvre votre création unique. Moment magique garanti ✨'
}];
const testimonials = [{
  name: 'Aminata Diallo',
  role: 'Pour l\'anniversaire de sa mère',
  avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Aminata',
  quote: 'J\'ai offert un QR Portrait à ma mère pour ses 60 ans. Quand elle a scanné le code et vu toutes nos photos de famille avec ma voix, elle a pleuré de joie. Un moment magique !',
  rating: 5
}, {
  name: 'Mamadou Kane',
  role: 'Cadeau pour sa fiancée',
  avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Mamadou',
  quote: 'Original et touchant. Ma fiancée a adoré ! Le cadre est de très belle qualité et le QR Code fonctionne parfaitement. C\'est devenu notre cadeau signature pour la famille.',
  rating: 5
}, {
  name: 'Fatou Sarr',
  role: 'Pour son meilleur ami',
  avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Fatou',
  quote: 'Super simple à créer, résultat incroyable ! Mon meilleur ami était vraiment ému. L\'idée du message audio rend le tout encore plus personnel. Je recommande à 100% !',
  rating: 5
}, {
  name: 'Ousmane Ndiaye',
  role: 'Cadeau de départ collègue',
  avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Ousmane',
  quote: 'Nous avons créé un QR Portrait collectif pour le départ d\'un collègue. Chacun a ajouté un mot, des photos d\'équipe... Il était très touché. Parfait pour les cadeaux de groupe !',
  rating: 5
}];
const faqs = [{
  question: 'Comment fonctionne un QR Portrait ?',
  answer: 'C\'est très simple ! Vous créez votre page personnalisée avec vos photos, messages et audio. Nous générons ensuite un QR Code unique qui, une fois scanné avec n\'importe quel smartphone, affiche votre création. Pas besoin d\'application spéciale, juste l\'appareil photo du téléphone.'
}, {
  question: 'Le QR Code fonctionne-t-il à vie ?',
  answer: 'Oui, absolument ! Contrairement aux abonnements mensuels, votre QR Portrait reste accessible à vie après un seul paiement de 10 000 F CFA. Vous et vos proches pourrez le consulter indéfiniment, sans limite de scans.'
}, {
  question: 'Combien de photos puis-je ajouter ?',
  answer: 'Il n\'y a aucune limite ! Vous pouvez ajouter autant de photos que vous le souhaitez. Elles défileront automatiquement en carrousel lorsque quelqu\'un scanne le QR Code. Parfait pour raconter une histoire complète.'
}, {
  question: 'Puis-je modifier mon QR Portrait après création ?',
  answer: 'Oui ! Vous pouvez à tout moment vous connecter à votre compte pour modifier les photos, le message texte ou l\'audio. Le QR Code reste le même, mais le contenu est mis à jour instantanément.'
}, {
  question: 'Comment fonctionne la livraison du cadre physique ?',
  answer: 'Si vous choisissez l\'option avec cadre physique (15 000 F CFA), nous imprimons votre QR Code sur un support premium et l\'encadrons dans un beau cadre photo. La livraison est incluse et prend 3-5 jours ouvrés à Dakar et environs.'
}, {
  question: 'Quels sont les moyens de paiement acceptés ?',
  answer: 'Nous acceptons Wave, Orange Money et MTN Money. Le paiement est sécurisé et vous recevez immédiatement accès à votre QR Portrait après confirmation du paiement.'
}, {
  question: 'Puis-je enregistrer plusieurs messages audio ?',
  answer: 'Pour le moment, vous pouvez enregistrer un message audio par QR Portrait. Nous recommandons d\'enregistrer un message principal qui représente bien votre intention. La durée n\'est pas limitée.'
}, {
  question: 'Le destinataire peut-il télécharger les photos ?',
  answer: 'Oui ! Lorsque quelqu\'un scanne le QR Code, il peut visualiser toutes les photos en carrousel et les télécharger individuellement sur son téléphone. C\'est un moyen parfait de partager des souvenirs.'
}];
const Landing = () => {
  const navigate = useNavigate();
  return <div className="min-h-screen bg-gradient-to-b from-background via-secondary/30 to-background">
      <Helmet>
        <title>QR Portrait - Créez des cadeaux personnalisés avec QR Code | Photos, Messages & Audio</title>
        <meta name="description" content="Créez un QR Portrait unique avec vos photos, messages personnalisés et audio. Le cadeau parfait pour vos proches. Offrez une expérience interactive et émouvante." />
        <meta name="keywords" content="QR Portrait, cadeau personnalisé, QR Code cadeau, photos personnalisées, message audio, cadeau couple, cadeau parents, cadeau original" />
        <link rel="canonical" href="https://qrportrait.lovable.app/" />
        <meta property="og:title" content="QR Portrait - Créez des cadeaux personnalisés uniques" />
        <meta property="og:description" content="Créez un QR Portrait avec vos photos, messages et audio. Le cadeau parfait pour vos proches." />
        <meta property="og:url" content="https://qrportrait.lovable.app/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="QR Portrait - Cadeaux personnalisés avec QR Code" />
        <meta name="twitter:description" content="Créez un QR Portrait unique avec vos photos, messages et audio pour vos proches." />
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "QR Portrait",
          "url": "https://qrportrait.lovable.app",
          "description": "Créez des cadeaux personnalisés uniques avec QR Code, photos, messages et audio",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://qrportrait.lovable.app/guides?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "QR Portrait",
          "url": "https://qrportrait.lovable.app",
          "logo": "https://qrportrait.lovable.app/favicon.ico",
          "description": "Plateforme de création de cadeaux personnalisés avec QR Code",
          "sameAs": [],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "availableLanguage": ["French"]
          }
        })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "QR Portrait",
          "description": "Cadeau personnalisé avec QR Code contenant photos, messages et audio",
          "brand": {
            "@type": "Brand",
            "name": "QR Portrait"
          },
          "offers": {
            "@type": "Offer",
            "price": "10000",
            "priceCurrency": "XOF",
            "availability": "https://schema.org/InStock"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "5",
            "ratingCount": "4",
            "bestRating": "5",
            "worstRating": "1"
          },
          "review": [
          {
            "@type": "Review",
            "author": { "@type": "Person", "name": "Aminata Diallo" },
            "reviewRating": { "@type": "Rating", "ratingValue": "5" },
            "reviewBody": "Un moment magique ! Ma mère a pleuré de joie en découvrant toutes nos photos de famille."
          },
          {
            "@type": "Review",
            "author": { "@type": "Person", "name": "Mamadou Kane" },
            "reviewRating": { "@type": "Rating", "ratingValue": "5" },
            "reviewBody": "Original et touchant. Ma fiancée a adoré ! Le cadre est de très belle qualité."
          },
          {
            "@type": "Review",
            "author": { "@type": "Person", "name": "Fatou Sarr" },
            "reviewRating": { "@type": "Rating", "ratingValue": "5" },
            "reviewBody": "Super simple à créer, résultat incroyable ! Je recommande à 100% !"
          },
          {
            "@type": "Review",
            "author": { "@type": "Person", "name": "Ousmane Ndiaye" },
            "reviewRating": { "@type": "Rating", "ratingValue": "5" },
            "reviewBody": "Parfait pour les cadeaux de groupe ! Notre collègue était très touché."
          }]

        })}
        </script>
      </Helmet>
      <Header />

      <HeroSection />

      {/* How it works */}
      <section id="how-it-works" className="py-20 px-6 lg:px-8 bg-gradient-to-b from-secondary/20 to-transparent">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
              Comment ça fonctionne ?
            </h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto">
              Créez votre QR Portrait en 4 étapes simples. Aucune compétence technique requise.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {steps.map((step, index) => <Card key={step.number} className="hover-lift glass-subtle animate-scale-in" style={{
            animationDelay: `${index * 0.1}s`
          }}>
                <CardContent className="p-6 space-y-3">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-2xl cta-gradient flex items-center justify-center soft-shadow-lg">
                      <span className="text-lg font-bold text-white">{step.number}</span>
                    </div>
                    <div className="flex-1 space-y-2">
                      <h3 className="text-lg font-bold text-foreground">{step.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
              Pourquoi choisir QR Portrait ?
            </h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto">
              Bien plus qu'un simple QR Code. Une expérience complète et mémorable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => <Card key={feature.title} className="hover-lift glass animate-scale-in text-center" style={{
            animationDelay: `${index * 0.1}s`
          }}>
                <CardContent className="p-6 space-y-3">
                  <div className="w-12 h-12 mx-auto rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center soft-shadow-sm">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-base font-bold text-foreground">{feature.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Themes Preview */}
      <section className="py-20 px-6 lg:px-8 bg-gradient-to-b from-secondary/20 to-transparent">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
              Une ambiance pour chaque relation
            </h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto">
              Choisissez le type de lien qui vous unit. Le design s'adapte automatiquement.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {themes.map((theme, index) => <Card key={theme.id} className="hover-lift glass-subtle animate-scale-in cursor-pointer group" style={{
            animationDelay: `${index * 0.1}s`
          }} onClick={() => navigate(OFFICIAL_CREATE_URL)}>
                <CardContent className="p-5 text-center space-y-3">
                  <div className="text-5xl group-hover:scale-110 transition-smooth">
                    {theme.emoji}
                  </div>
                  <div>
                    <div className="font-bold text-foreground text-sm">{theme.label}</div>
                    <div className="text-xs text-muted-foreground mt-1">{theme.description}</div>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      <TestimonialsSection />


      {/* FAQ */}
      <section className="py-20 px-6 lg:px-8 bg-gradient-to-b from-secondary/20 to-transparent">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
              Questions fréquentes
            </h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto">
              Tout ce que vous devez savoir sur les QR Portraits
            </p>
          </div>

          <CardGlass className="soft-shadow-lg">
            <CardContent className="p-6">
              <Accordion type="single" collapsible className="w-full space-y-3">
                {faqs.map((faq, index) => <AccordionItem key={index} value={`item-${index}`} className="border-0 glass-subtle rounded-2xl px-5 animate-fade-in hover-lift" style={{
                animationDelay: `${index * 0.05}s`
              }}>
                    <AccordionTrigger className="text-left text-sm font-semibold text-foreground hover:text-primary transition-smooth py-4 hover:no-underline">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </CardContent>
          </CardGlass>

          {/* Contact CTA */}
          <div className="mt-12 text-center glass rounded-3xl p-6 soft-shadow">
            <p className="text-sm text-muted-foreground mb-3">
              Vous avez d'autres questions ?
            </p>
            <Button variant="outline" onClick={() => window.location.href = 'mailto:contact@qrportrait.com'} className="gap-2 text-sm">
              <MessageSquare className="w-4 h-4" />
              Contactez-nous
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
              Tarification simple et transparente
            </h2>
            <p className="text-base text-muted-foreground">
              Pas d'abonnement. Un seul paiement pour un accès à vie.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* QR Portrait Simple */}
            <Card className="hover-lift glass-strong">
              <CardContent className="p-8 space-y-5">
                <div className="space-y-2">
                  <h3 className="text-lg font-bold text-foreground">QR Portrait Simple</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold bg-gradient-to-r from-primary to-primary-dark bg-clip-text text-transparent">
                      3 500
                    </span>
                    <span className="text-base text-muted-foreground">F CFA</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Paiement unique • Accès à vie</p>
                </div>

                <div className="space-y-2.5">
                  {['QR Code personnalisé', 'Photos illimitées', 'Message audio', 'Texte personnalisé', 'Thèmes adaptatifs', 'Partage illimité'].map((item) => <div key={item} className="flex items-center gap-2.5">
                      <div className="w-4 h-4 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-2.5 h-2.5 text-primary" />
                      </div>
                      <span className="text-xs text-foreground">{item}</span>
                    </div>)}
                </div>

                <Button variant="outline" className="w-full" onClick={() => navigate(OFFICIAL_CREATE_URL)}>
                  Commencer
                </Button>
              </CardContent>
            </Card>

            {/* QR Portrait + Accessoire */}
            <Card className="hover-lift glass-strong border-2 border-primary/20 relative overflow-hidden">
              <div className="absolute top-3 right-3 bg-primary text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
                POPULAIRE
              </div>
              <CardContent className="p-8 space-y-5">
                <div className="space-y-2">
                  <h3 className="text-lg font-bold text-foreground">QR Portrait + Accessoire</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl font-bold text-muted-foreground">à partir de</span>
                    <span className="text-3xl font-bold bg-gradient-to-r from-primary to-primary-dark bg-clip-text text-transparent">
                       

 



 

8 500
                    </span>
                    <span className="text-base text-muted-foreground">F CFA</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Prix selon l'accessoire choisi • Accès à vie</p>
                </div>

                <div className="space-y-2.5">
                  {['Tout du QR Portrait Simple', 'Cadre photo ou autre accessoire', 'QR Code imprimé', 'Livraison à domicile', 'Emballage cadeau', 'Support client prioritaire'].map((item) => <div key={item} className="flex items-center gap-2.5">
                      <div className="w-4 h-4 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                        <Check className="w-2.5 h-2.5 text-white" />
                      </div>
                      <span className="text-xs text-foreground font-medium">{item}</span>
                    </div>)}
                </div>

                <Button variant="premium" className="w-full" onClick={() => navigate(OFFICIAL_CREATE_URL)}>
                  Créer avec accessoire
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="glass-strong soft-shadow-xl hover-lift">
            <CardContent className="p-10 lg:p-12 text-center space-y-6">
              <div className="space-y-3">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
                  Prêt à créer votre premier
                  <br />
                  QR Portrait ?
                </h2>
                <p className="text-base text-muted-foreground max-w-2xl mx-auto">
                  Rejoignez ceux qui ont déjà choisi d'offrir des souvenirs qui durent.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="premium" onClick={() => navigate(OFFICIAL_CREATE_URL)} className="gap-2 group py-[10px]">
                  Commencer maintenant
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-smooth" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate('/demo')} className="py-[10px]">
                  Voir la démo
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-8 border-t border-border/50 bg-secondary/20">
        <div className="max-w-6xl mx-auto text-center space-y-4">
          <div className="flex items-center justify-center gap-2.5">
            <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-primary to-primary-light flex items-center justify-center">
              <Heart className="w-3.5 h-3.5 text-white" fill="white" />
            </div>
            <span className="text-base font-bold text-foreground">QR Portrait</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Fait avec ❤️ pour créer des souvenirs inoubliables
          </p>
          <div className="flex flex-wrap items-center justify-center gap-5 text-xs text-muted-foreground">
            <button onClick={() => navigate('/guides')} className="hover:text-primary transition-colors">
              Guides d'utilisation
            </button>
            <span className="text-border">•</span>
            <button onClick={() => navigate('/faq')} className="hover:text-primary transition-colors">
              FAQ
            </button>
            <span className="text-border">•</span>
            <button className="hover:text-primary transition-colors">Contact</button>
            <span className="text-border">•</span>
            <button onClick={() => navigate('/terms')} className="hover:text-primary transition-colors">CGU</button>
            <span className="text-border">•</span>
            <button onClick={() => navigate('/privacy')} className="hover:text-primary transition-colors">Confidentialité</button>
          </div>
          <p className="text-[10px] text-muted-foreground/60">
            © 2025 QR Portrait. Tous droits réservés.
          </p>
        </div>
      </footer>
    </div>;};export default Landing;